import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  styled,
  IconButton
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import { projectService } from '../../services/project';
import { useToast } from '../../contexts/ToastContext';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#1E2E46',
    color: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '480px',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const DeleteIconContainer = styled(Box)(({ theme }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 87, 87, 0.1)',
  color: '#ff5757',
  margin: '16px auto',
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.6)',
  '&:hover': {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  textAlign: 'center',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
}));

const StyledCancelButton = styled(Button)(({ theme }) => ({
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '8px',
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

const StyledDeleteButton = styled(Button)(({ theme }) => ({
  color: 'white',
  backgroundColor: '#ff5757',
  borderRadius: '8px',
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#ff3b3b',
  },
}));

interface DeleteProjectModalProps {
  open: boolean;
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteProjectModal: React.FC<DeleteProjectModalProps> = ({ 
  open, 
  projectId, 
  projectName, 
  onClose,
  onSuccess
}) => {
  const { showError } = useToast();
  const [loading, setLoading] = useState(false);


  const handleDelete = async () => {
    setLoading(true);

    
    try {
      await projectService.deleteProject(projectId);
      
      // Se chegou aqui, a exclusão foi bem-sucedida
      setLoading(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      setLoading(false);
      showError(error.message || 'Não foi possível excluir o projeto. Tente novamente mais tarde.');
      console.error('Erro ao excluir projeto:', error);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogTitle>
        <Typography variant="h6" component="div" fontWeight={600}>
          Excluir Projeto
        </Typography>
        <StyledCloseButton 
          onClick={onClose}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </StyledCloseButton>
      </StyledDialogTitle>
      
      <StyledDialogContent>
        <DeleteIconContainer>
          <DeleteForeverIcon style={{ fontSize: 30 }} />
        </DeleteIconContainer>
        
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Tem certeza que deseja excluir este projeto?
        </Typography>
        
        <DialogContentText color="rgba(255, 255, 255, 0.7)">
          O projeto "<strong>{projectName}</strong>" será excluído permanentemente, junto com todos os seus quadros, colunas e cartões. Esta ação não pode ser desfeita.
        </DialogContentText>

      </StyledDialogContent>
      
      <StyledDialogActions>
        <StyledCancelButton 
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </StyledCancelButton>
        <StyledDeleteButton
          onClick={handleDelete}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Excluindo...' : 'Excluir Projeto'}
        </StyledDeleteButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DeleteProjectModal;